
































































































































































































































































import {
	computed,
	defineComponent,
	Ref,
	ref,
	watch,
} from '@vue/composition-api';
import { useConfirmDialog } from '@vueuse/core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import ColumnContainer from '@front-app/components/layouts/columns/ColumnContainer.vue';
import ConfirmationModal from '@front-app/components/modals/ConfirmationModal.vue';
import CtaButton from '@front-app/components/buttons/CtaButton.vue';
import EditorPanels from '@front-app/components/userProfile/editing/EditorPanels.vue';
import ErrorCard from '@front-app/components/cards/state/ErrorCard.vue';
import LoaderState from '@front-app/components/LoaderState.vue';
import PaddedContent from '@front-app/components/layouts/columns/PaddedContent.vue';
import PersonalInformation from '@front-app/components/userProfile/main/PersonalInformation.vue';
import PhoneLink from '@front-app/components/PhoneLink.vue';
import PresentOccupation from '@front-app/components/userProfile/main/PresentOccupation.vue';
import ProfileRoles from '@front-app/components/userProfile/main/ProfileRoles.vue';
import ProfileSpecialties from '@front-app/components/userProfile/main/ProfileSpecialties.vue';
import ProfileInterests from '@front-app/components/userProfile/main/ProfileInterests.vue';
import ProfileSummary from '@front-app/components/userProfile/main/ProfileSummary.vue';
import ScrollingColumn from '@front-app/components/layouts/columns/ScrollingColumn.vue';
import SimpleCard from '@front-app/components/cards/SimpleCard.vue';
import UserAssociations from '@front-app/components/userProfile/main/UserAssociations.vue';
import UserAvatar from '@front-app/components/user/avatar.vue';
import { CurrentUserStore, UserAvatarStore, UserStore } from '@front-app/store/singletons/User';
import { EditableSection } from '@front-app/components/userProfile/editableProfileSections';
import { FocusableElement } from '@front-app/hooks/useAsyncModal';
import { formatPhoneNumber } from '@front-app/util/format';
import { getUsername, checkIfUserIsEmpty, hasValidRolesForSpecialty } from '@front-app/util/user';
import { ImageScale } from '@front-app/services/ImageApi/ImageModel';
import { OrganizationsStore } from '@front-app/store/singletons/Organizations';
import { Size, Spacing } from '@front-app/components/constants';
import { useAsyncStatus } from '@front-app/hooks/useAsyncStatus';
import { UserProfileOptionsStore } from '@front-app/store/singletons/UserProfileOptions';
import { UserDetailRoleNoNone } from '@front-app/services/UserApi/UserModel';
import { useTranslate } from '@front-app/hooks/useTranslate';

export default defineComponent({
	components: {
		ColumnContainer,
		ConfirmationModal,
		CtaButton,
		EditorPanels,
		ErrorCard,
		FontAwesomeIcon,
		LoaderState,
		PaddedContent,
		PersonalInformation,
		PhoneLink,
		PresentOccupation,
		ProfileRoles,
		ProfileSpecialties,
		ProfileInterests,
		ProfileSummary,
		ScrollingColumn,
		SimpleCard,
		UserAssociations,
		UserAvatar,
	},
	props: {
		userId: {
			type: String,
			required: true,
		},
	},
	setup(props, ctx) {
		const userStore = UserStore.getInstance();
		const currentUserStore = CurrentUserStore;

		const {
			track: trackFetchingUser,
			isLoading: isFetchingUser,
			isError: isFetchingUserError,
		} = useAsyncStatus();

		watch(() => props.userId, (curr: string, prev: string | undefined) => {
			if (curr === prev) {
				return;
			}

			if (!curr) {
				trackFetchingUser(userStore.fetch(currentUserStore.currentUser.value.id));
			} else {
				trackFetchingUser(userStore.fetch(curr));
			}
		}, { immediate: true });

		// TF-118 Disable editing for now, until TF-131
		const isCurrentUserProfile = computed(() => {
			if (!props.userId) {
				return true;
			}
			return props.userId === currentUserStore.currentUser.value.id;
		});

		const {
			track: trackFetchingCurrentUser,
			isLoading: isFetchingCurrentUser,
			isError: isFetchingCurrentUserError,
		} = useAsyncStatus();

		const fetchUserAndEmploymentInfo = async () => {
			UserProfileOptionsStore.fetch();
			OrganizationsStore.fetchAllPartial();
		};

		watch(isCurrentUserProfile, (val: boolean) => {
			if (val) {
				trackFetchingCurrentUser(fetchUserAndEmploymentInfo());
			}
		}, { immediate: true });
		const user = computed(() => {
			if (isCurrentUserProfile.value) {
				return currentUserStore.currentUser.value;
			}
			return userStore.getFullUser(props.userId).value;
		});
		const username = computed(() => getUsername(ctx.root.$t.bind(ctx.root), user.value));
		const organizationLogo = computed(() => {
			const { logoSrc } = user.value.currentOccupation.employer;
			// Extra variable is needed here, or else the env var is not properly
			// interpolated for some reason 🤷‍♀️
			const baseUrl = process.env.VUE_APP_TRAPP_PUBLIC_URL;
			return logoSrc ? `${baseUrl}${logoSrc}.png` : '';
		});

		const isEmptyUser = computed(() => checkIfUserIsEmpty(user.value));
		const canSelectSpecialties = computed(() => hasValidRolesForSpecialty(user.value.roles));

		// Edit modal
		const returnTo: Ref<null | FocusableElement> = ref(null);
		const currentEditSection: Ref<EditableSection> = ref(EditableSection.NONE);
		const currentEditExperienceId: Ref<undefined | number> = ref(undefined);
		const openEditModal = async (targetSection: EditableSection, newReturnTo: FocusableElement) => {
			currentEditExperienceId.value = undefined;
			returnTo.value = newReturnTo;
			currentEditSection.value = targetSection;
		};
		const openEditExperienceModal = async (experienceId: number, newReturnTo: FocusableElement) => {
			currentEditExperienceId.value = experienceId;
			returnTo.value = newReturnTo;
			currentEditSection.value = EditableSection.PROFESSIONAL_EXPERIENCE;
		};
		// END edit modal

		const $t = useTranslate(ctx);
		const nonPrimaryRoles = computed(() => user.value.roles
			.filter((r) => r !== user.value.primaryRole)
			.map((role) => $t(role as string) as unknown as string));

		// Roles and specialties
		const confirmUpdateRolesModal = useConfirmDialog();
		const confirmAddSpecialtiesModal = useConfirmDialog();
		const selectedRoles = ref([] as UserDetailRoleNoNone[]);
		const numSelectedAvailableRoles = ref(0);

		const openConfirmRolesModal = (event: { roles: UserDetailRoleNoNone[]; numSelectedRoles: number }) => {
			selectedRoles.value = event.roles;
			numSelectedAvailableRoles.value = event.numSelectedRoles;
			confirmUpdateRolesModal.reveal();
		};

		const {
			track: trackUpdatingRoles,
			isLoading: isUpdatingRoles,
			isSuccess: isSuccessUpdatingRoles,
		} = useAsyncStatus();

		const {
			track: trackRetryUpdatingRoles,
			isError: isErrorRetryUpdatingRoles,
			isLoading: isRetryUpdatingRoles,
			isSuccess: isSuccessRetryUpdatingRoles,
		} = useAsyncStatus();

		const saveRolesOnConfirm = async () => {
			try {
				await trackUpdatingRoles(CurrentUserStore.actions.updateRoles(selectedRoles.value));
			} catch (error: unknown) {
				// Retry updating roles once in case of a server error (Hibernate error on the back-end)
				await trackRetryUpdatingRoles(CurrentUserStore.actions.updateRoles(selectedRoles.value));
			}

			confirmUpdateRolesModal.confirm();

			// Display confirmation modal to prompt user to add specialties for the following valid roles
			if (hasValidRolesForSpecialty(selectedRoles.value)) {
				confirmAddSpecialtiesModal.reveal();
			}
		};

		const confirmAddingSpecialties = () => {
			confirmAddSpecialtiesModal.confirm();
			currentEditSection.value = EditableSection.SPECIALTIES;
		};

		return {
			nonPrimaryRoles,
			isCurrentUserProfile,
			user,
			username,
			organizationLogo,
			isFetchingUser,
			isFetchingUserError,
			isFetchingCurrentUser,
			isFetchingCurrentUserError,
			canSelectSpecialties,
			isEmptyUser,
			// Editing
			EditableSection,
			currentEditSection,
			currentEditExperienceId,
			returnTo,
			openEditModal,
			openEditExperienceModal,
			// Avatar
			fetchAvatar: UserAvatarStore.fetch,
			getAvatar: UserAvatarStore.get,
			// Roles and specialties
			confirmUpdateRolesModal,
			confirmAddSpecialtiesModal,
			numSelectedAvailableRoles,
			openConfirmRolesModal,
			saveRolesOnConfirm,
			confirmAddingSpecialties,
			isUpdatingRoles,
			isSuccessUpdatingRoles,
			isErrorRetryUpdatingRoles,
			isRetryUpdatingRoles,
			isSuccessRetryUpdatingRoles,
			// Util
			formatPhoneNumber,
			Size,
			Spacing,
			ImageScale_2X: ImageScale.LARGE_2X,
		};
	},
});
